/* Confirmación tras bajada de precio: listado de leads + pregunta antes del envío masivo. */
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from './ThemeProvider';
import { etiquetaEtapaLead, formatImporteRebaja } from '../utils/bajadaPrecioUtils';
import { fuenteVisibleEnLead, procedenciaChipStyle } from '../utils/procedenciaColor';

/** Mismas columnas que Fríos + etapa pipeline. */
const COLUMNAS = [
  { id: 'nombre', label: 'Nombre', width: 200 },
  { id: 'telefono', label: 'Teléfono', width: 140 },
  { id: 'email', label: 'Email', width: 220 },
  { id: 'operacion', label: 'Operación', width: 100 },
  { id: 'propiedad_ref', label: 'Ref.', width: 100 },
  { id: 'fuente', label: 'Procedencia', width: 130 },
  { id: 'agente_ultimo_nombre', label: 'Agente último', width: 160 },
  { id: 'agente_anterior_nombre', label: 'Agente anterior', width: 160 },
  { id: 'created_at', label: 'Fecha / hora', width: 132 },
  { id: 'etapa', label: 'Etapa', width: 100 },
];

function refLeadEntrada(item = {}) {
  const directa = String(item.propiedad_ref || '').trim();
  if (directa) return directa;
  const ex =
    typeof item.datos_extras === 'object' && item.datos_extras !== null
      ? item.datos_extras
      : (() => {
          try {
            return JSON.parse(item.datos_extras || '{}');
          } catch {
            return {};
          }
        })();
  return String(ex.referencia || ex.propiedad_ref || ex.referencia_propiedad || '').trim() || null;
}

function fechaReferenciaLead(item) {
  return item?.fecha_cita || item?.created_at || null;
}

function renderCelda(item, colId, { c, isDark, textMain, textSoft }) {
  if (colId === 'nombre') {
    return (
      <span style={{ fontWeight: 600, color: textMain }}>
        {item.nombre} {item.apellido || ''}
      </span>
    );
  }
  if (colId === 'telefono') {
    return <span style={{ color: textSoft }}>{item.telefono || '—'}</span>;
  }
  if (colId === 'email') {
    return <span style={{ color: textSoft }}>{item.email || '—'}</span>;
  }
  if (colId === 'operacion') {
    if (!item.operacion) return '—';
    const col = item.operacion.toLowerCase() === 'alquiler' ? c.accentPink : c.accentGreen;
    const bg = item.operacion.toLowerCase() === 'alquiler'
      ? (c.accentPinkLight || '#fce7f3')
      : (c.accentGreenLight || '#f0fdf4');
    return (
      <span style={{ fontSize: 11, fontWeight: 700, color: col, background: bg, padding: '2px 7px', borderRadius: 5 }}>
        {item.operacion}
      </span>
    );
  }
  if (colId === 'fuente') {
    const fuente = fuenteVisibleEnLead(item.fuente);
    if (!fuente) return '—';
    return (
      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap', ...procedenciaChipStyle(fuente) }}>
        {fuente}
      </span>
    );
  }
  if (colId === 'agente_ultimo_nombre') {
    const ultimo = item.agente_ultimo_nombre || item.agente_nombre || null;
    const esAna = ultimo && (/^ana$/i.test(String(ultimo).trim()) || /asistente virtual/i.test(String(ultimo)));
    return (
      <span style={{ color: esAna ? '#10b981' : (isDark ? '#93c5fd' : c.accentBlue), fontWeight: 500 }}>
        {ultimo || '—'}
      </span>
    );
  }
  if (colId === 'agente_anterior_nombre') {
    const ultimo = item.agente_ultimo_nombre || null;
    const prev = item.agente_anterior_nombre;
    if (!prev || (ultimo && String(prev).toLowerCase() === String(ultimo).toLowerCase())) {
      return <span style={{ color: textSoft, fontWeight: 500 }}>—</span>;
    }
    return <span style={{ color: isDark ? '#93c5fd' : c.accentBlue, fontWeight: 500 }}>{prev}</span>;
  }
  if (colId === 'propiedad_ref') {
    const ref = refLeadEntrada(item);
    return ref
      ? <span style={{ fontWeight: 700, color: isDark ? '#93c5fd' : c.accentBlue }}>{ref}</span>
      : '—';
  }
  if (colId === 'created_at') {
    const ref = fechaReferenciaLead(item);
    if (!ref) return '—';
    const d = new Date(ref);
    const fechaStr = d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' });
    const horaStr = d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    return (
      <span style={{ whiteSpace: 'nowrap', color: textSoft }} title={d.toLocaleString('es-ES')}>
        {fechaStr}
        <span style={{ color: isDark ? '#94a3b8' : c.textMuted, marginLeft: 6 }}>{horaStr}</span>
      </span>
    );
  }
  if (colId === 'etapa') {
    return <span style={{ color: textMain, fontWeight: 600 }}>{etiquetaEtapaLead(item.etapa)}</span>;
  }
  return '—';
}

export default function ModalConfirmLeadsBajadaPrecio({
  isOpen,
  leads = [],
  bajada = null,
  referencia = '',
  onConfirm,
  onCancel,
}) {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === 'dark';
  const textMain = isDark ? '#ffffff' : c.textPrimary;
  const textSoft = isDark ? '#f1f5f9' : c.textSecondary;

  const leadIds = useMemo(() => leads.map((l) => l.id).filter(Boolean), [leads]);
  const [selectedIds, setSelectedIds] = useState(() => new Set(leadIds));

  useEffect(() => {
    if (isOpen) setSelectedIds(new Set(leadIds));
  }, [isOpen, leadIds.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const importeFmt = bajada?.importe != null ? formatImporteRebaja(bajada.importe) : '';
  const todosSeleccionados = selectedIds.size === leads.length && leads.length > 0;
  const leadsSeleccionados = leads.filter((l) => selectedIds.has(l.id));

  const toggleTodos = (checked) => {
    setSelectedIds(checked ? new Set(leadIds) : new Set());
  };

  const toggleUno = (id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10050,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '95vw',
          maxWidth: 1200,
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          background: isDark ? '#0f172a' : c.bgCard,
          borderRadius: 14,
          border: `1px solid ${isDark ? 'rgba(100,200,255,0.2)' : c.border}`,
          boxShadow: '0 24px 48px rgba(0,0,0,0.35)',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '18px 20px 12px', borderBottom: `1px solid ${c.border}` }}>
          <div style={{ fontSize: 13, color: '#16a34a', fontWeight: 600, marginBottom: 6 }}>
            Propiedad actualizada correctamente
          </div>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: textMain }}>
            Bajada de precio{importeFmt ? ` · ${importeFmt}` : ''}
            {referencia ? ` · Ref. ${referencia}` : ''}
          </h2>
          <p style={{ margin: '10px 0 0', fontSize: 14, lineHeight: 1.5, color: textSoft }}>
            Todos estos clientes han visitado o se han interesado por esta propiedad.
            ¿Quieres que les mandemos un WhatsApp o mail informando de esta bajada?
          </p>
          {leads.length > 0 && (
            <p style={{ margin: '8px 0 0', fontSize: 12, color: isDark ? '#cbd5e1' : c.textMuted }}>
              {selectedIds.size} de {leads.length} seleccionado{selectedIds.size !== 1 ? 's' : ''}
            </p>
          )}
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '0 12px 12px' }}>
          {leads.length === 0 ? (
            <p style={{ padding: 16, color: textSoft, textAlign: 'center' }}>No hay leads para notificar.</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${c.border}` }}>
                  <th style={{ padding: '9px 10px', width: 40, textAlign: 'center', background: isDark ? '#1e293b' : '#f1f5f9', borderRight: `1px solid ${c.border}` }}>
                    <input
                      type="checkbox"
                      checked={todosSeleccionados}
                      onChange={(e) => toggleTodos(e.target.checked)}
                      style={{ cursor: 'pointer', width: 14, height: 14 }}
                    />
                  </th>
                  {COLUMNAS.map((col) => (
                    <th
                      key={col.id}
                      style={{
                        padding: '9px 12px',
                        textAlign: 'left',
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        color: isDark ? '#e2e8f0' : c.textMuted,
                        background: isDark ? '#1e293b' : '#f1f5f9',
                        borderRight: `1px solid ${c.border}`,
                        whiteSpace: 'nowrap',
                        minWidth: col.width,
                      }}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => {
                  const isSelected = selectedIds.has(l.id);
                  const bgSelected = isDark ? '#1e3a5f' : '#eff6ff';
                  return (
                    <tr
                      key={l.id}
                      style={{
                        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : c.border}`,
                        background: isSelected ? bgSelected : 'transparent',
                      }}
                    >
                      <td
                        style={{ padding: '4px 10px', textAlign: 'center', borderRight: `1px solid ${c.border}` }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => toggleUno(l.id, e.target.checked)}
                          style={{ cursor: 'pointer', width: 14, height: 14 }}
                        />
                      </td>
                      {COLUMNAS.map((col) => (
                        <td
                          key={col.id}
                          style={{
                            padding: '9px 12px',
                            borderRight: `1px solid ${c.border}`,
                            whiteSpace: 'nowrap',
                            maxWidth: col.width,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {renderCelda(l, col.id, { c, isDark, textMain, textSoft })}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
            padding: '14px 20px',
            borderTop: `1px solid ${c.border}`,
          }}
        >
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '9px 18px',
              borderRadius: 8,
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.35)' : c.border}`,
              background: 'transparent',
              color: textMain,
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            No, gracias
          </button>
          <button
            type="button"
            onClick={() => onConfirm?.(leadsSeleccionados)}
            disabled={!leadsSeleccionados.length}
            style={{
              padding: '9px 18px',
              borderRadius: 8,
              border: 'none',
              background: leadsSeleccionados.length ? '#16a34a' : '#94a3b8',
              color: '#fff',
              cursor: leadsSeleccionados.length ? 'pointer' : 'not-allowed',
              fontWeight: 600,
            }}
          >
            Sí, preparar envío{leadsSeleccionados.length ? ` (${leadsSeleccionados.length})` : ''}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
